<template>
  <div>
    <!-- Guide Dialog -->
    <v-dialog
      v-model="showGuide"
      max-width="550px"
      content-class="guide-dialog-content"
      @click:outside="closeGuide"
    >
      <v-card>
        <!-- Header -->
        <v-card-title class="guide-header primary white--text">
          <v-icon left dark>mdi-compass</v-icon>
          <span class="headline">Booking Guide</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeGuide" class="guide-close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Content -->
        <v-card-text class="pa-4">
          <!-- Progress -->
          <div class="text-center mb-3">
            <span class="caption grey--text">Step {{ currentStep + 1 }} of {{ steps.length }}</span>
            <v-progress-linear 
              :value="((currentStep + 1) / steps.length) * 100"
              color="primary"
              height="4"
              class="mt-1"
              rounded
            ></v-progress-linear>
          </div>

          <!-- Step Content -->
          <div class="text-center pa-3">
            <v-icon x-large :color="steps[currentStep].color" class="mb-3">
              {{ steps[currentStep].icon }}
            </v-icon>
            <h3 class="title font-weight-bold mb-2">{{ steps[currentStep].title }}</h3>
            <p class="subtitle-1 grey--text text--darken-1">{{ steps[currentStep].description }}</p>
            
            <!-- Optional badge for optional steps -->
            <v-chip 
              v-if="steps[currentStep].optional"
              small 
              color="grey lighten-2" 
              text-color="grey darken-2"
              class="mt-1 mb-2"
            >
              <v-icon left small>mdi-information-outline</v-icon>
              Optional Step
            </v-chip>
            
            <!-- Visual Tab Hint for Steps 4, 5, 6 -->
            <div v-if="currentStep >= 3 && currentStep <= 5" class="tab-hint">
              <div class="tab-hint-icons">
                <span class="tab-icon" :class="{ active: currentStep === 3 }">🏕️</span>
                <span class="tab-arrow">→</span>
                <span class="tab-icon" :class="{ active: currentStep === 4 }">🏨</span>
                <span class="tab-arrow">→</span>
                <span class="tab-icon" :class="{ active: currentStep === 5 }">⛺</span>
              </div>
              <div class="tab-hint-labels">
                <span class="tab-label" :class="{ active: currentStep === 3 }">Activities</span>
                <span class="tab-label" :class="{ active: currentStep === 4 }">Accommodation</span>
                <span class="tab-label" :class="{ active: currentStep === 5 }">Camping</span>
              </div>
              <div class="tab-hint-text">👇 Click the tabs below to explore (optional)</div>
            </div>

            <v-chip 
              small 
              :color="steps[currentStep].color" 
              text-color="white"
              class="mt-2"
            >
              {{ steps[currentStep].action }}
            </v-chip>
          </div>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="pa-4">
          <v-btn 
            v-if="currentStep > 0"
            text 
            @click="prevStep"
            class="text-capitalize"
            small
          >
            <v-icon left small>mdi-chevron-left</v-icon> Back
          </v-btn>
          <v-btn 
            v-else
            text 
            @click="closeGuide"
            class="text-capitalize"
            small
            color="grey"
          >
            Skip Guide
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            v-if="currentStep === steps.length - 1"
            color="success" 
            @click="finishGuide"
            class="text-capitalize"
            small
          >
            Got It! <v-icon right small>mdi-check</v-icon>
          </v-btn>
          <v-btn 
            v-else
            color="primary" 
            @click="nextStep"
            class="text-capitalize"
            small
          >
            Next <v-icon right small>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  export default {
    name: 'UserGuide',
    data() {
      return {
        showGuide: false,
        currentStep: 0,
        steps: [
          {
            icon: 'mdi-calendar-range',
            title: 'Step 1: Select Your Dates',
            description: 'Choose your check-in and check-out dates. This unlocks guest and vehicle options for your safari.',
            action: '📅 Pick your travel dates',
            color: 'primary',
            optional: false
          },
          {
            icon: 'mdi-account-group',
            title: 'Step 2: Add Your Guests',
            description: 'Click "Number of Guests" to add adults and children. Auto-saves to your cart instantly!',
            action: '👥 Add your guests',
            color: 'info',
            optional: false
          },
          {
            icon: 'mdi-car',
            title: 'Step 3: Add Your Vehicles',
            description: 'Select vehicle types and quantities. Or check "already booked separately" if vehicle has been booked in a different booking.',
            action: '🚗 Add your vehicles',
            color: 'warning',
            optional: false
          },
          {
            icon: 'mdi-hiking',
            title: 'Step 4: Browse Activities (Optional)',
            description: 'Go to the Activities tab and browse exciting experiences like game drives, chimpanzee tracking, and more. Click "Book Now" to add to your cart. Skip if you\'re on a day trip!',
            action: '🏕️ Book activities',
            color: 'deep-purple',
            optional: true
          },
          {
            icon: 'mdi-hotel',
            title: 'Step 5: Choose Accommodation (Optional)',
            description: 'Switch to the Accommodation tab. Select from luxury lodges, cottages, or guest houses. Each shows availability and pricing. Not needed for day trips.',
            action: '🏨 Pick accommodation',
            color: 'teal',
            optional: true
          },
          {
            icon: 'mdi-tent',
            title: 'Step 6: Select Camping (Optional)',
            description: 'Go to the Camping tab. Choose campsites for a true safari experience. Self-catering and full-board options available. Optional for day trips.',
            action: '⛺ Choose campsite',
            color: 'orange',
            optional: true
          },
          {
            icon: 'mdi-cart',
            title: 'Step 7: Review & Checkout',
            description: 'Click the cart button to review ALL selections: dates, guests, vehicles, activities, accommodation, and camping. Proceed to checkout when ready. Only what you\'ve booked will appear.',
            action: '🛒 Review and checkout',
            color: 'success',
            optional: false
          }
        ]
      }
    },
    methods: {
      openGuide() {
        this.currentStep = 0
        this.showGuide = true
        document.body.style.overflow = 'hidden'
        
        this.$nextTick(() => {
          const guideElement = document.querySelector('.guide-dialog-content')
          if (guideElement) {
            guideElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      },

      closeGuide() {
        this.showGuide = false
        document.body.style.overflow = ''
      },

      finishGuide() {
        this.showGuide = false
        document.body.style.overflow = ''
        this.$emit('finished')
      },

      nextStep() {
        if (this.currentStep < this.steps.length - 1) {
          this.currentStep++
        }
      },

      prevStep() {
        if (this.currentStep > 0) {
          this.currentStep--
        }
      }
    },
    mounted() {
      // ✅ ONLY listen for manual trigger from help button - NO auto-open!
      this.$root.$on('show-guide', this.openGuide)
    },
    beforeDestroy() {
      this.$root.$off('show-guide', this.openGuide)
      document.body.style.overflow = ''
    }
  }
</script>

<style scoped>
  .guide-dialog-content {
    border-radius: 12px !important;
    overflow: hidden !important;
  }

  .guide-header {
    background: linear-gradient(135deg, #74a533, #5a8a2a) !important;
    border-radius: 0 !important;
  }

  .guide-close-btn {
    background: rgba(255,255,255,0.1) !important;
    border-radius: 50% !important;
  }

  .guide-close-btn:hover {
    background: rgba(255,255,255,0.2) !important;
  }

  .text-capitalize {
    text-transform: none !important;
  }

  /* ============ TAB VISUAL HINT ============ */
  .tab-hint {
    background: #f5f7fa;
    border-radius: 8px;
    padding: 12px 16px;
    margin: 8px 0 4px 0;
    border: 2px dashed rgba(116, 165, 51, 0.3);
    animation: fadeInUp 0.5s ease-out;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .tab-hint-icons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 22px;
  }

  .tab-icon {
    opacity: 0.4;
    transition: all 0.3s ease;
    padding: 4px 8px;
    border-radius: 4px;
  }

  .tab-icon.active {
    opacity: 1;
    transform: scale(1.2);
    background: rgba(116, 165, 51, 0.2);
    padding: 4px 12px;
    border-radius: 8px;
  }

  .tab-arrow {
    color: #ccc;
    font-size: 14px;
  }

  .tab-hint-labels {
    display: flex;
    justify-content: center;
    gap: 12px;
    font-size: 9px;
    color: #999;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  .tab-label {
    padding: 2px 6px;
    border-radius: 4px;
    transition: all 0.3s ease;
  }

  .tab-label.active {
    color: #74a533;
    background: rgba(116, 165, 51, 0.15);
    font-weight: 700;
  }

  .tab-hint-text {
    text-align: center;
    font-size: 10px;
    color: #74a533;
    font-weight: 600;
    margin-top: 4px;
    letter-spacing: 0.3px;
  }

  /* ============ RESPONSIVE ============ */
  @media (max-width: 600px) {
    .guide-dialog-content {
      width: 95% !important;
      max-width: 400px !important;
      margin: 10px !important;
    }

    .guide-header .headline {
      font-size: 1.1rem !important;
    }

    .title {
      font-size: 1rem !important;
    }

    .subtitle-1 {
      font-size: 0.85rem !important;
    }

    .tab-hint-icons {
      font-size: 18px;
      gap: 4px;
    }

    .tab-arrow {
      font-size: 12px;
    }

    .tab-hint-labels {
      font-size: 8px;
      gap: 6px;
    }

    .tab-hint-text {
      font-size: 9px;
    }

    .tab-hint {
      padding: 8px 10px;
    }
  }

  @media (max-width: 400px) {
    .tab-hint-icons {
      font-size: 14px;
    }

    .tab-hint-labels {
      font-size: 7px;
      gap: 4px;
    }

    .tab-hint-text {
      font-size: 8px;
    }
  }
</style>